'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import apiClient from '@/lib/api-client'

type NotificationType = 'sla_warning' | 'sla_overdue' | 'new_assignment' | 'rfi_received' | 'da_ready_for_review' | 'da_document_ready' | 'da_document_failed' | 'da_document_approved' | 'rebuttal_received' | 'system' | string

interface NotificationItem {
  id: string
  type: NotificationType
  title: string
  message: string
  is_read: boolean
  read_at: string | null
  related_entity_type: string | null
  related_entity_id: string | null
  created_at: string | null
}

const typeConfig: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  sla_warning: { icon: Icons.alertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  sla_overdue: { icon: Icons.alertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  new_assignment: { icon: Icons.user, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  assignment: { icon: Icons.user, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  assignment_rejected: { icon: Icons.userMinus, color: 'text-red-500', bg: 'bg-red-500/10' },
  // MRM-facing: a claim reached the next review level and needs assigning (D24 AC1)
  assignment_required: { icon: Icons.inbox, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  rfi_received: { icon: Icons.fileQuestion, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  da_ready_for_review: { icon: Icons.fileText, color: 'text-brand-cornflower', bg: 'bg-brand-cornflower/10' },
  rebuttal_received: { icon: Icons.flag, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  harm_hold_triggered: { icon: Icons.alertCircle, color: 'text-red-600', bg: 'bg-red-600/10' },
  harm_hold_resolved: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  claim_resumed_from_hold: { icon: Icons.user, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  // CR item 14: QA finished a claim; the reviewer it scored can read their scorecard.
  qa_review_completed: { icon: Icons.star, color: 'text-green-600', bg: 'bg-green-500/10' },
  fwa_disagreement_flagged: { icon: Icons.flag, color: 'text-rose-500', bg: 'bg-rose-500/10' },
  // Due date extension notifications
  extension_requested: { icon: Icons.calendar, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  extension_approved: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  extension_denied: { icon: Icons.x, color: 'text-red-500', bg: 'bg-red-500/10' },
  // SLA clock resumed after an RFI and the review due date moved (FRD US-G04 AC1)
  due_date_recalculated: { icon: Icons.calendar, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  // Escalation notifications
  escalation_created: { icon: Icons.messageSquare, color: 'text-rose-500', bg: 'bg-rose-500/10' },
  escalation_response: { icon: Icons.messageCircle, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  escalation_resolved: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  // RFI Response Callback (Supervity Auto operator)
  rfi_response_received: { icon: Icons.inbox, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  // RFI MR Manager review workflow
  rfi_pending_review: { icon: Icons.mail, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  rfi_sent: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  rfi_rejected: { icon: Icons.xCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  // DA Worksheet document generation notifications
  da_document_ready: { icon: Icons.fileCheck, color: 'text-green-600', bg: 'bg-green-500/10' },
  da_document_failed: { icon: Icons.alertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  da_document_approved: { icon: Icons.fileCheck, color: 'text-green-600', bg: 'bg-green-500/10' },
  // ── DA Worksheet approval chain (client DA process, Defect 34) ────────────
  // One entry per stage the worksheet can arrive at. Amber = "you must act",
  // blue = "it moved on", green = done, red = an approval was undone. Kept in
  // sync with app/services/da_notifications.STAGE_NOTIFICATIONS — a type emitted
  // there but missing here renders a generic icon with no deep link, which
  // tests/test_da_notifications.py asserts against.
  da_l1_task_assigned: { icon: Icons.edit, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  // A manager moved the worksheet's L1 steps to another L1 reviewer (CR item 16).
  da_l1_reassigned: { icon: Icons.users, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_submitted_to_qam: { icon: Icons.inbox, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_sent_for_l1_cmd_approval: { icon: Icons.userCheck, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_awaiting_second_approval: { icon: Icons.users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  da_changes_requested: { icon: Icons.pencil, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  da_pending_mrm_review: { icon: Icons.shield, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_mrm_edits_to_apply: { icon: Icons.pencil, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_ready_to_finalize: { icon: Icons.fileCheck, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_pending_pm_approval: { icon: Icons.userCheck, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_ready_for_submission: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  // Retired per-claim packaging types (D36): no longer produced, kept so
  // historical rows still render and deep-link.
  da_package_prep_started: { icon: Icons.folderOpen, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  da_package_prep_required: { icon: Icons.archive, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  da_package_zipped: { icon: Icons.archive, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  da_package_submitted: { icon: Icons.send, color: 'text-green-600', bg: 'bg-green-500/10' },
  da_reopened: { icon: Icons.undo, color: 'text-red-500', bg: 'bg-red-500/10' },
  // Sample-level submission (D36). Deep-link to the sample's Submission tab.
  sample_ready_for_submission: { icon: Icons.checkCircle, color: 'text-green-600', bg: 'bg-green-500/10' },
  sample_submission_pending_approval: { icon: Icons.mail, color: 'text-amber-600', bg: 'bg-amber-500/10' },
  sample_submission_sent: { icon: Icons.send, color: 'text-green-600', bg: 'bg-green-500/10' },
  sample_submission_failed: { icon: Icons.alertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  sample_submission_invalidated: { icon: Icons.refresh, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  // Legacy: emitted by the pre-Defect-34 DA flow. No longer produced, but
  // historical rows still exist in deployed environments and must render.
  da_task_created: { icon: Icons.fileText, color: 'text-brand-cornflower', bg: 'bg-brand-cornflower/10' },
  system: { icon: Icons.info, color: 'text-blue-500', bg: 'bg-blue-500/10' },
}

/**
 * Maps a notification type to the URL the user should land on when clicking it.
 * Returns null if there is no meaningful deep-link for this notification type.
 */
function getNotificationUrl(notification: NotificationItem): string | null {
  const claimId = notification.related_entity_id

  switch (notification.type) {
    // Escalations Q&A deep link directly to the escalations tab on the claim details
    case 'escalation_created':
    case 'escalation_response':
    case 'escalation_resolved':
      return claimId ? `/claims/${claimId}?tab=escalations` : null

    // MRM sees this — take them straight to the Extensions sub-tab
    case 'extension_requested':
      return '/workbench?tab=assignment&subtab=extensions'

    // Reviewer sees these — take them to the specific claim to see the outcome
    case 'extension_approved':
    case 'extension_denied':
      return claimId ? `/claims/${claimId}` : '/workbench?tab=queue'

    // New claim assignment — reviewer goes to their queue or the specific claim
    case 'new_assignment':
    case 'assignment':
      return claimId ? `/claims/${claimId}` : '/workbench?tab=queue'

    // Assignment rejected — MRM goes to specific claim or assignment tab
    case 'assignment_rejected':
      return claimId ? `/claims/${claimId}` : '/workbench?tab=assignment'

    // A claim reached the next review level and needs the MRM to assign it
    // (D24 AC1). Land on the Unassigned list — that is where the Assign action
    // lives — not on the claim page, which has no assign control. Pre-filter
    // to the specific level and claim so the MRM doesn't have to hunt through
    // every pending claim at every level for the one that triggered this.
    case 'assignment_required': {
      // notify_assignment_required's title is always
      // "{target_level} assignment required — {claim_number}"
      // (app/services/notification_service.py) — there's no separate
      // metadata field carrying the level, so it's parsed from the one place
      // it's guaranteed to be, in the exact format this codebase writes it.
      const level = notification.title.match(/^(\S+) assignment required/)?.[1]
      const params = new URLSearchParams({ tab: 'assignment', subtab: 'unassigned' })
      if (level) params.set('level', level)
      if (claimId) params.set('claimId', claimId)
      return `/workbench?${params.toString()}`
    }

    // SLA alerts — go to the specific claim
    case 'sla_warning':
    case 'sla_overdue':
      return claimId ? `/claims/${claimId}` : null

    // RFI, DA, rebuttal — go to the specific claim
    case 'rfi_received':
    case 'rfi_response_received':
    case 'rebuttal_received':
    // The reviewer who raised the RFI needs the claim, not the RFI Manager:
    // these tell them what happened to their request.
    case 'rfi_sent':
    case 'rfi_rejected':
    // The MRM decides on the claim whether the internal due date follows.
    case 'due_date_recalculated':
      return claimId ? `/claims/${claimId}` : null

    // An MRM acts on a pending draft in the RFI Manager, so send them there.
    // Falls back to the claim if the notification carries no RFI queue context.
    case 'rfi_pending_review':
      return claimId ? `/rfis?claim=${encodeURIComponent(claimId)}` : '/rfis'

    case 'da_ready_for_review':
      if (notification.related_entity_type === 'da_worksheet') {
        return `/disagreements/worksheets/${notification.related_entity_id}`
      }
      return claimId ? `/claims/${claimId}` : '/disagreements'

    // DA Worksheet document ready/failed/approved — go to detail page directly
    case 'da_document_ready':
    case 'da_document_failed':
    case 'da_document_approved':
    // Every stage of the DA approval chain (Defect 34). All of these ask the
    // recipient to look at, or act on, one specific worksheet — so they all deep
    // link to that worksheet's detail page, where the action panel for their
    // role is rendered. Falling back to the list would make the recipient hunt
    // for the claim the notification just named.
    case 'da_l1_task_assigned':
    case 'da_l1_reassigned':
    case 'da_submitted_to_qam':
    case 'da_sent_for_l1_cmd_approval':
    case 'da_awaiting_second_approval':
    case 'da_changes_requested':
    case 'da_pending_mrm_review':
    case 'da_mrm_edits_to_apply':
    case 'da_ready_to_finalize':
    case 'da_pending_pm_approval':
    case 'da_ready_for_submission':
    case 'da_package_prep_started':
    case 'da_package_prep_required':
    case 'da_package_zipped':
    case 'da_package_submitted':
    case 'da_reopened':
      if (notification.related_entity_type === 'da_worksheet') {
        return `/disagreements/worksheets/${notification.related_entity_id}`
      }
      return '/disagreements'

    // Sample-level submission (D36): the sample's Submission tab.
    case 'sample_ready_for_submission':
    case 'sample_submission_pending_approval':
    case 'sample_submission_sent':
    case 'sample_submission_failed':
    case 'sample_submission_invalidated':
      return notification.related_entity_type === 'sample' && notification.related_entity_id
        ? `/samples/${notification.related_entity_id}?tab=submission`
        : '/submissions'

    // Legacy pre-Defect-34 type, scoped to the claim rather than the worksheet.
    case 'da_task_created':
      return claimId ? `/claims/${claimId}` : '/disagreements'

    // Harm hold — go to the specific claim
    case 'harm_hold_triggered':
      return claimId ? `/claims/${claimId}` : null

    // Harm hold resolved — go to the specific claim (auditor / MRM sees outcome)
    case 'harm_hold_resolved':
      return claimId ? `/claims/${claimId}` : null

    // Claim resumed — reviewer goes directly to the claim to continue their review
    case 'claim_resumed_from_hold':
      return claimId ? `/claims/${claimId}` : '/workbench?tab=queue'

    // QA review complete (CR item 14): straight to the reviewer's scorecard.
    case 'qa_review_completed':
      return claimId ? `/claims/${claimId}?tab=qa-scorecard` : null

    default:
      return null
  }
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface NotificationRowProps {
  notification: NotificationItem
  onMarkRead: (id: string) => void
  onNavigate: (url: string) => void
}

function NotificationRow({ notification, onMarkRead, onNavigate }: NotificationRowProps) {
  const config = typeConfig[notification.type] || typeConfig.system
  const Icon = config.icon
  const url = getNotificationUrl(notification)
  const isClickable = !!url

  const handleClick = () => {
    onMarkRead(notification.id)
    if (url) onNavigate(url)
  }

  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl p-3 transition-all duration-200 ease-out',
        isClickable
          ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98] hover:bg-brand-cornflower/5 hover:shadow-sm'
          : 'cursor-default',
        notification.is_read ? 'opacity-60 hover:opacity-100' : ''
      )}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <div className={cn('flex-shrink-0 rounded-lg p-2', config.bg)}>
        <Icon className={cn('h-4 w-4', config.color)} strokeWidth={1.5} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='truncate text-sm font-medium text-foreground'>{notification.title}</p>
          <div className='flex items-center gap-1 shrink-0'>
            {!notification.is_read && <span className='mt-1.5 h-2 w-2 animate-pulse rounded-full bg-brand-cornflower' />}
            {isClickable && (
              <Icons.chevronRight className='mt-1.5 h-3 w-3 text-muted-foreground/40' strokeWidth={1.5} />
            )}
          </div>
        </div>
        <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>{notification.message}</p>
        <p className='mt-1 text-[10px] text-muted-foreground/60'>{formatRelativeTime(notification.created_at)}</p>
      </div>
    </div>
  )
}

export function NotificationCenter() {
  const router = useRouter()
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [isOpen, setIsOpen] = React.useState(false)

  const fetchNotifications = React.useCallback(async () => {
    try {
      const res = await apiClient('/api/notifications?limit=50') as { notifications: NotificationItem[]; unread_count?: number }
      const items = res.notifications || []
      setNotifications(items)
      // Always sync badge from the list response to eliminate any drift with the poll
      const liveUnread = typeof res.unread_count === 'number'
        ? res.unread_count
        : items.filter(n => !n.is_read).length
      setUnreadCount(liveUnread)
    } catch { /* graceful */ }
  }, [])

  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const res = await apiClient('/api/notifications/count') as { unread_count: number }
      setUnreadCount(res.unread_count || 0)
    } catch { /* graceful */ }
  }, [])

  // Poll the unread count every 2 minutes (was 20 s; RELI UAT 2026-10-09
  // asked for far fewer background refreshes). The list itself loads on open.
  React.useEffect(() => {
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 2 * 60_000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  const markAsRead = React.useCallback(async (id: string) => {
    try {
      await apiClient(`/api/notifications/${id}/read`, { method: 'POST' })
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch { /* graceful */ }
  }, [])

  const markAllAsRead = async () => {
    try {
      await apiClient('/api/notifications/read-all', { method: 'POST' })
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
      setUnreadCount(0)
    } catch { /* graceful */ }
  }

  /** Close the popover and route to the target URL */
  const handleNavigate = React.useCallback((url: string) => {
    setIsOpen(false)
    router.push(url)
  }, [router])

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open)
    // Refresh the full notification list every time the panel opens
    if (open) fetchNotifications()
  }, [fetchNotifications])

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant='ghost' size='icon' className='relative rounded-full text-muted-foreground hover:text-foreground'>
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className={cn('absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white animate-badge-bounce')}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>Notifications</h3>
          {unreadCount > 0 && (
            <Button variant='link' size='sm' onClick={markAllAsRead} className='h-auto p-0 text-xs text-brand-cornflower'>
              Mark all as read
            </Button>
          )}
        </div>
        <div className='max-h-[380px] overflow-y-auto p-2'>
          {notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle className='h-6 w-6 text-muted-foreground' />
              </div>
              <p className='text-sm font-medium text-foreground'>All caught up!</p>
              <p className='mt-1 text-xs text-muted-foreground'>No new notifications</p>
            </div>
          ) : (
            <div className='space-y-1'>
              {notifications.map(n => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onMarkRead={markAsRead}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

